import _ from 'lodash';
import { GUID_REGEX_PATTERN } from '@/main/app.constants';

/**
 * Generates a base64-encoded GUID using browser crypto APIs.
 *
 * Note: By design, this function produces a guid in a different format from the GUIDs generated by
 * the backend. This is to avoid confusing a frontend-generated guid with a backend-generated one, and so that
 * regexes designed to find backend-generated guids will not unintentionally match frontend-generated guids.
 *
 * @example dcWyqxUedy-c5owG_HIlsA
 * @returns The generated GUID (22 characters long)
 */
export function base64guid(): string {
  const cryptoObj = window.crypto;
  const array = new Uint8Array(16); // 128 bits of randomness
  cryptoObj.getRandomValues(array);
  return (
    window
      .btoa(String.fromCharCode.apply(null, Array.from(array)))
      // Remove url unsafe characters and padding
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  );
}

/**
 * Validates a GUID string.
 *
 * @param guid - The GUID to check
 * @example af8a8416-6e18-a307-bd9c-f2c947bbb3aa
 * @returns true if a valid GUID, false otherwise
 */
export function validateGuid(guid: unknown): guid is string {
  const re = new RegExp(`^${GUID_REGEX_PATTERN}$`);
  return _.isString(guid) && re.test(guid);
}

/**
 * Extracts all GUIDs from a string.
 *
 * @param s - The string from which to extract
 * @returns Array of GUIDs
 */
export function extractGuids(s: string): RegExpMatchArray | string[] {
  const re = new RegExp(GUID_REGEX_PATTERN, 'g');
  return `${s}`.match(re) || [];
}

/**
 * Generates a random UUID using the browser's crypto API with a fallback implementation.
 *
 * @example af8a8416-6e18-a307-bd9c-f2c947bbb3aa
 * @returns A UUID string in standard format
 */
export function randomUUID(): string {
  // Safety check because of the warning about only being available in HTTPS context on some browsers according to MDN:
  // https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID#browser_compatibility
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback using a Vanilla JS implementation that's good enough for our use here
  let dt = new Date().getTime();
  let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let r = ((dt + Math.random() * 16) % 16) | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
}
